/**
 * Screen-space reflections — view-space raymarch with geometric step growth and
 * a binary refinement, GGX-importance-sampled by material roughness (blue-noise
 * dithered), followed by a roughness-weighted separable blur that stands in for
 * a reflection cone trace.
 *
 * The result is consumed inside the forward shading pass one frame later, where
 * it is blended over the environment-probe radiance by confidence — so a missed
 * or off-screen ray falls back to the probe with no seam and no double counting.
 *
 * owner: render
 */
import * as THREE from 'three';
import { Blitter, makeMaterial, makeRT, GLSL_COMMON } from './Common.js';

const SSR_FRAG = /* glsl */`
precision highp float;
${GLSL_COMMON}

uniform sampler2D tColor;
uniform sampler2D tDepth;
uniform sampler2D tNormal;
uniform sampler2D tNoise;
uniform mat4 uProj;
uniform mat4 uInvProj;
uniform vec2 uNoiseScale;
uniform vec2 uTexel;
uniform float uNear;
uniform float uMaxDistance;
uniform float uThickness;
uniform float uFrame;
uniform float uMaxRoughness;
uniform float uIntensity;

varying vec2 vUv;

vec3 importanceGGX(vec2 Xi, float rough, vec3 N) {
  float a = max(rough * rough, 1e-3);
  float phi = 2.0 * PI_ * Xi.x;
  float ct = sqrt((1.0 - Xi.y) / (1.0 + (a * a - 1.0) * Xi.y));
  float st = sqrt(max(0.0, 1.0 - ct * ct));
  vec3 H = vec3(st * cos(phi), st * sin(phi), ct);
  vec3 up = abs(N.z) < 0.999 ? vec3(0.0, 0.0, 1.0) : vec3(1.0, 0.0, 0.0);
  vec3 T = normalize(cross(up, N));
  vec3 B = cross(N, T);
  return normalize(T * H.x + B * H.y + N * H.z);
}

void main() {
  float d = texture2D(tDepth, vUv).x;
  vec4 nrm = texture2D(tNormal, vUv);
  float rough = unpackRough(nrm.w);

  if (d >= 0.99999 || rough > uMaxRoughness) { gl_FragColor = vec4(0.0); return; }

  vec3 P = viewFromDepth(vUv, d, uInvProj);
  vec3 N = normalize(nrm.xyz);
  vec3 V = normalize(-P);
  float viewZ = -P.z;

  vec4 bn = texture2D(tNoise, vUv * uNoiseScale);
  vec2 Xi = vec2(fract(bn.b + uFrame * 0.6180339887498949),
                 fract(bn.a + uFrame * 0.4142135623730951));
  float jitter = fract(bn.r + uFrame * 0.7548776662466927);

  vec3 H = importanceGGX(Xi, min(rough, 0.55), N);
  vec3 R = reflect(-V, H);
  if (dot(R, N) < 0.02) R = reflect(-V, N);

  // rays coming back at the camera cannot be resolved on screen
  float facing = saturate(1.0 - dot(R, V));
  if (facing < 0.02) { gl_FragColor = vec4(0.0); return; }

  vec3 origin = P + N * (0.01 + 0.0035 * viewZ);

  float stepLen = uMaxDistance / float(PF_SSR_STEPS) * 0.55;
  float t = stepLen * (0.35 + 0.65 * jitter);
  float prevT = 0.0;
  float hit = 0.0;
  vec2 hitUv = vec2(0.0);
  float hitDiff = 0.0;

  for (int i = 0; i < PF_SSR_STEPS; i++) {
    vec3 sp = origin + R * t;
    if (sp.z > -uNear) break;
    vec4 clip = uProj * vec4(sp, 1.0);
    vec2 uv = (clip.xy / clip.w) * 0.5 + 0.5;
    if (uv.x < 0.0 || uv.x > 1.0 || uv.y < 0.0 || uv.y > 1.0) break;

    float sd = texture2D(tDepth, uv).x;
    if (sd < 0.99999) {
      float sceneZ = -viewFromDepth(uv, sd, uInvProj).z;
      float rayZ = -sp.z;
      float diff = rayZ - sceneZ;
      if (diff > 0.0 && diff < uThickness + stepLen * 0.5) {
        hit = 1.0;
        hitUv = uv;
        hitDiff = diff;
        break;
      }
    }
    prevT = t;
    t += stepLen;
    stepLen *= 1.22;
  }

  if (hit < 0.5) { gl_FragColor = vec4(0.0); return; }

  // binary refinement between the last miss and the hit
  float lo = prevT, hi = t;
  for (int i = 0; i < PF_SSR_REFINE; i++) {
    float mid = 0.5 * (lo + hi);
    vec3 sp = origin + R * mid;
    vec4 clip = uProj * vec4(sp, 1.0);
    vec2 uv = (clip.xy / clip.w) * 0.5 + 0.5;
    float sd = texture2D(tDepth, uv).x;
    float sceneZ = -viewFromDepth(uv, sd, uInvProj).z;
    if (-sp.z > sceneZ) { hi = mid; hitUv = uv; hitDiff = -sp.z - sceneZ; }
    else lo = mid;
  }

  vec3 col = texture2D(tColor, hitUv).rgb;
  col = min(col, vec3(24.0)); // firefly clamp — a single bright texel must not seed a streak

  // confidence
  vec2 e = abs(hitUv * 2.0 - 1.0);
  float edge = saturate(1.0 - pow(max(e.x, e.y), 6.0));
  edge *= smoothstep(0.0, 0.12, min(min(hitUv.x, hitUv.y), min(1.0 - hitUv.x, 1.0 - hitUv.y)));
  float distFade = 1.0 - saturate(hi / uMaxDistance);
  distFade = distFade * distFade * 0.5 + 0.5;
  float thick = 1.0 - saturate(hitDiff / max(uThickness, 1e-3));
  float roughFade = smoothstep(uMaxRoughness, uMaxRoughness * 0.45, rough);

  float conf = edge * distFade * mix(0.55, 1.0, thick) * roughFade * saturate(facing * 2.5) * uIntensity;
  gl_FragColor = vec4(col, saturate(conf));
}
`;

const SSR_BLUR_FRAG = /* glsl */`
precision highp float;
${GLSL_COMMON}
uniform sampler2D tSSR;
uniform sampler2D tNormal;
uniform vec2 uDir;
varying vec2 vUv;

void main() {
  vec4 c = texture2D(tSSR, vUv);
  float rough = unpackRough(texture2D(tNormal, vUv).w);
  float radius = 1.0 + rough * 9.0;
  vec4 sum = c * 0.3;
  float wsum = 0.3;
  const float wgt[3] = float[3](0.26, 0.14, 0.05);
  for (int i = 0; i < 3; i++) {
    vec2 o = uDir * radius * float(i + 1);
    vec4 a = texture2D(tSSR, vUv + o);
    vec4 b = texture2D(tSSR, vUv - o);
    sum += (a + b) * wgt[i];
    wsum += 2.0 * wgt[i];
  }
  gl_FragColor = sum / wsum;
}
`;

export class SSR {
  constructor(blitter) {
    this.blit = blitter || new Blitter();
    this.rtA = null; this.rtB = null;
    this.w = 0; this.h = 0;

    this.mat = makeMaterial({
      name: 'postfx.ssr',
      fragmentShader: SSR_FRAG,
      defines: { PF_SSR_STEPS: 28, PF_SSR_REFINE: 5 },
      uniforms: {
        tColor: { value: null }, tDepth: { value: null }, tNormal: { value: null }, tNoise: { value: null },
        uProj: { value: new THREE.Matrix4() },
        uInvProj: { value: new THREE.Matrix4() },
        uNoiseScale: { value: new THREE.Vector2() },
        uTexel: { value: new THREE.Vector2() },
        uNear: { value: 0.02 },
        uMaxDistance: { value: 34.0 },
        uThickness: { value: 0.55 },
        uFrame: { value: 0 },
        uMaxRoughness: { value: 0.62 },
        uIntensity: { value: 1.0 },
      },
    });

    this.matBlur = makeMaterial({
      name: 'postfx.ssr.blur',
      fragmentShader: SSR_BLUR_FRAG,
      uniforms: {
        tSSR: { value: null }, tNormal: { value: null },
        uDir: { value: new THREE.Vector2() },
      },
    });
  }

  setQuality(steps, refine) {
    if (this.mat.defines.PF_SSR_STEPS === steps && this.mat.defines.PF_SSR_REFINE === refine) return;
    this.mat.defines.PF_SSR_STEPS = steps;
    this.mat.defines.PF_SSR_REFINE = refine;
    this.mat.needsUpdate = true;
  }

  setSize(w, h) {
    const hw = Math.max(1, w >> 1), hh = Math.max(1, h >> 1);
    if (this.w === hw && this.h === hh) return;
    this.w = hw; this.h = hh;
    if (this.rtA) this.rtA.dispose();
    if (this.rtB) this.rtB.dispose();
    this.rtA = makeRT(hw, hh, { name: 'postfx.ssr.a' });
    this.rtB = makeRT(hw, hh, { name: 'postfx.ssr.b' });
  }

  get texture() { return this.rtA.texture; }

  render(renderer, o) {
    const u = this.mat.uniforms;
    u.tColor.value = o.colorTexture;
    u.tDepth.value = o.depthTexture;
    u.tNormal.value = o.normalTexture;
    u.tNoise.value = o.noiseTexture;
    u.uProj.value.copy(o.proj);
    u.uInvProj.value.copy(o.invProj);
    u.uNoiseScale.value.set(this.w / (o.noiseSize || 64), this.h / (o.noiseSize || 64));
    u.uTexel.value.set(1 / this.w, 1 / this.h);
    u.uNear.value = o.near;
    u.uFrame.value = o.frame % 64;
    u.uIntensity.value = o.intensity;
    this.blit.draw(renderer, this.mat, this.rtA);

    const b = this.matBlur.uniforms;
    b.tNormal.value = o.normalTexture;
    b.tSSR.value = this.rtA.texture;
    b.uDir.value.set(1 / this.w, 0);
    this.blit.draw(renderer, this.matBlur, this.rtB);
    b.tSSR.value = this.rtB.texture;
    b.uDir.value.set(0, 1 / this.h);
    this.blit.draw(renderer, this.matBlur, this.rtA);
  }

  dispose() {
    if (this.rtA) this.rtA.dispose();
    if (this.rtB) this.rtB.dispose();
    this.mat.dispose(); this.matBlur.dispose();
  }
}
